import { Preferences } from '@capacitor/preferences';
import {
  PushNotifications,
  type ActionPerformed,
  type PushNotificationSchema,
  type Token,
} from '@capacitor/push-notifications';
import { getStorage } from 'forstok-ui-lib';

import {
  RegisterNotificationDeviceDocument,
  type RegisterNotificationDeviceInput,
} from '../../graphqls/dest/graphql';
import type { TUser } from '../../types/share';
import cookies from '../cookies';
import { client } from '../forstok';
import { isNativeAndroidApp } from './platform';
import {
  clearStoredPendingPushTarget as clearStoredPendingPushTargetValue,
  getStoredPendingPushTarget as getStoredPendingPushTargetValue,
  parsePushNotificationTarget,
  storePendingPushTarget,
  type TPushNotificationTarget,
} from './pushNotificationTarget';

const PUSH_DEBUG_STORAGE_KEYS = {
  token: 'forstok.push.token',
  lastNotification: 'forstok.push.lastNotification',
  lastAction: 'forstok.push.lastAction',
  lastRegistrationError: 'forstok.push.lastRegistrationError',
} as const;

const PUSH_INTERNAL_STORAGE_KEYS = {
  installationId: 'forstok.push.installationId',
} as const;

const PUSH_APP_NAME = 'Forstok Chat';

type TPushDebugWindow = Window & {
  __forstokPushDebug?: {
    token?: string;
    lastNotification?: PushNotificationSchema;
    lastAction?: ActionPerformed;
    lastRegistrationError?: string;
    pendingTarget?: TPushNotificationTarget;
  };
};

let listenersPromise: Promise<void> | null = null;
let registerPromise: Promise<void> | null = null;
let unregisterPromise: Promise<void> | null = null;
let isRegistered = false;
let syncDevicePromise: Promise<void> | null = null;
let syncDeviceFingerprintInFlight = '';
let lastSyncedDeviceFingerprint = '';

// TEMPORARY NOTE:
// Keep push registration available before login so opening the Android app is enough to generate and inspect an FCM device token
const ENABLE_GENERATE_TOKEN_BEFORE_LOGIN = true;

const getPushDebugWindow = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window as TPushDebugWindow;
};

const updatePushDebugWindow = (nextValue: Partial<NonNullable<TPushDebugWindow['__forstokPushDebug']>>) => {
  const pushDebugWindow = getPushDebugWindow();

  if (!pushDebugWindow) {
    return;
  }

  pushDebugWindow.__forstokPushDebug = {
    ...pushDebugWindow.__forstokPushDebug,
    ...nextValue,
  };
};

const createInstallationId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `install-${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
};

const storeInternalValue = async (key: keyof typeof PUSH_INTERNAL_STORAGE_KEYS, value: string) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(PUSH_INTERNAL_STORAGE_KEYS[key], value);
  }

  await Preferences.set({
    key: PUSH_INTERNAL_STORAGE_KEYS[key],
    value,
  });
};

const getStoredInternalValue = async (key: keyof typeof PUSH_INTERNAL_STORAGE_KEYS) => {
  if (typeof window !== 'undefined') {
    const cachedValue = window.localStorage.getItem(PUSH_INTERNAL_STORAGE_KEYS[key]);
    if (cachedValue) {
      return cachedValue;
    }
  }

  const storedValue = await Preferences.get({
    key: PUSH_INTERNAL_STORAGE_KEYS[key],
  });

  return storedValue.value || '';
};

const getOrCreatePushInstallationId = async () => {
  const storedInstallationId = await getStoredInternalValue('installationId');

  if (storedInstallationId) {
    return storedInstallationId;
  }

  const nextInstallationId = createInstallationId();
  await storeInternalValue('installationId', nextInstallationId);
  return nextInstallationId;
};

const storePushDebugValue = async (key: keyof typeof PUSH_DEBUG_STORAGE_KEYS, value: string) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(PUSH_DEBUG_STORAGE_KEYS[key], value);
  }

  await Preferences.set({
    key: PUSH_DEBUG_STORAGE_KEYS[key],
    value,
  });
};

const removePushDebugValue = async (key: keyof typeof PUSH_DEBUG_STORAGE_KEYS) => {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(PUSH_DEBUG_STORAGE_KEYS[key]);
  }

  await Preferences.remove({
    key: PUSH_DEBUG_STORAGE_KEYS[key],
  });
};

const getStoredPushDebugValue = async (key: keyof typeof PUSH_DEBUG_STORAGE_KEYS) => {
  if (typeof window !== 'undefined') {
    const cachedValue = window.localStorage.getItem(PUSH_DEBUG_STORAGE_KEYS[key]);
    if (cachedValue) {
      return cachedValue;
    }
  }

  const storedValue = await Preferences.get({
    key: PUSH_DEBUG_STORAGE_KEYS[key],
  });

  return storedValue.value || '';
};

const getAuthenticatedPushUser = () => {
  return getStorage('users', 'object') as TUser | undefined;
};

const syncNotificationDeviceRegistration = async (tokenOverride?: string) => {
  if (!isNativeAndroidApp()) {
    return;
  }

  const appToken = (cookies.get('token') as string | undefined) || '';
  const pushToken = tokenOverride || (await getStoredPushToken());
  const users = getAuthenticatedPushUser();
  const email = users?.email || '';
  const profileId = typeof users?.profile_id === 'number' ? users.profile_id : undefined;

  if (!appToken || !pushToken || !email) {
    return;
  }

  const syncFingerprint = JSON.stringify({
    email,
    profileId: profileId ?? null,
    pushToken,
  });

  if (syncFingerprint === lastSyncedDeviceFingerprint) {
    return;
  }

  if (syncDevicePromise && syncDeviceFingerprintInFlight === syncFingerprint) {
    return syncDevicePromise;
  }

  syncDeviceFingerprintInFlight = syncFingerprint;
  syncDevicePromise = (async () => {
    const deviceId = await getOrCreatePushInstallationId();
    const input: RegisterNotificationDeviceInput = {
      email,
      fcmToken: pushToken,
      platform: 'android',
      deviceId,
      appName: PUSH_APP_NAME,
      isActive: true,
      ...(typeof profileId === 'number' ? { profileId } : {}),
    };

    const result = await client.mutate({
      mutation: RegisterNotificationDeviceDocument,
      variables: { input },
    });

    const payload = result.data?.registerNotificationDevice;

    if (!payload?.success) {
      const rawMessage: unknown = payload?.message;
      const errorMessage =
        typeof rawMessage === 'string' && rawMessage
          ? rawMessage
          : 'registerNotificationDevice returned an unsuccessful response.';
      throw new Error(errorMessage);
    }

    lastSyncedDeviceFingerprint = syncFingerprint;
    console.info('[Push] Notification device synced:', {
      email,
      profileId: profileId ?? null,
      deviceId,
      appName: PUSH_APP_NAME,
    });
  })()
    .catch((error) => {
      console.error('[Push] Failed to sync notification device.', error);
      throw error;
    })
    .finally(() => {
      syncDevicePromise = null;
      syncDeviceFingerprintInFlight = '';
    });

  return syncDevicePromise;
};

const hydratePushDebugWindow = async () => {
  if (!isNativeAndroidApp()) {
    return;
  }

  const [token, lastNotification, lastAction, lastRegistrationError, pendingTarget] = await Promise.all([
    getStoredPushDebugValue('token'),
    getStoredPushDebugValue('lastNotification'),
    getStoredPushDebugValue('lastAction'),
    getStoredPushDebugValue('lastRegistrationError'),
    getStoredPendingPushTargetValue(),
  ]);

  updatePushDebugWindow({
    token: token || undefined,
    lastNotification: lastNotification ? (JSON.parse(lastNotification) as PushNotificationSchema) : undefined,
    lastAction: lastAction ? (JSON.parse(lastAction) as ActionPerformed) : undefined,
    lastRegistrationError: lastRegistrationError || undefined,
    pendingTarget: pendingTarget || undefined,
  });
};

const onRegistration = async (token: Token) => {
  isRegistered = true;
  await storePushDebugValue('token', token.value);
  updatePushDebugWindow({
    token: token.value,
    lastRegistrationError: undefined,
  });
  console.info('[Push] Registration token:', token.value);
  await syncNotificationDeviceRegistration(token.value);
};

const onRegistrationError = async (error: { error: string }) => {
  isRegistered = false;
  await storePushDebugValue('lastRegistrationError', error.error);
  updatePushDebugWindow({
    lastRegistrationError: error.error,
  });
  console.error('[Push] Registration error:', error.error);
};

const onPushNotificationReceived = async (notification: PushNotificationSchema) => {
  await storePushDebugValue('lastNotification', JSON.stringify(notification));
  updatePushDebugWindow({
    lastNotification: notification,
  });
  console.info('[Push] Notification received:', notification);
};

const onPushNotificationActionPerformed = async (action: ActionPerformed) => {
  await storePushDebugValue('lastAction', JSON.stringify(action));
  const pendingTarget = parsePushNotificationTarget(action.notification.data);
  if (pendingTarget) {
    await storePendingPushTarget(pendingTarget);
  }
  updatePushDebugWindow({
    lastAction: action,
    pendingTarget: pendingTarget || undefined,
  });
  console.info('[Push] Notification action performed:', action);
  if (pendingTarget) {
    console.info('[Push] Pending room target stored:', pendingTarget);
  }
};

const ensurePushListeners = async () => {
  if (!isNativeAndroidApp()) {
    return;
  }

  if (listenersPromise) {
    return listenersPromise;
  }

  listenersPromise = (async () => {
    await PushNotifications.addListener('registration', (token) => {
      void onRegistration(token);
    });
    await PushNotifications.addListener('registrationError', (error) => {
      void onRegistrationError(error);
    });
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      void onPushNotificationReceived(notification);
    });
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      void onPushNotificationActionPerformed(action);
    });
    await hydratePushDebugWindow();
  })();

  return listenersPromise;
};

const registerPushNotifications = async () => {
  if (!isNativeAndroidApp() || registerPromise || isRegistered) {
    return registerPromise;
  }

  registerPromise = (async () => {
    let permissionStatus = await PushNotifications.checkPermissions();

    console.info('[Push] Current permission status:', permissionStatus.receive);

    if (permissionStatus.receive === 'prompt') {
      permissionStatus = await PushNotifications.requestPermissions();
      console.info('[Push] Permission request result:', permissionStatus.receive);
    }

    if (permissionStatus.receive !== 'granted') {
      console.warn('[Push] Notification permission not granted. Skipping registration.');
      return;
    }

    await PushNotifications.register();
    console.info('[Push] Push registration requested.');
  })();

  try {
    await registerPromise;
  } finally {
    registerPromise = null;
  }
};

const unregisterPushNotifications = async () => {
  if (!isNativeAndroidApp() || unregisterPromise) {
    return unregisterPromise;
  }

  unregisterPromise = (async () => {
    try {
      await PushNotifications.unregister();
      console.info('[Push] Push notifications unregistered.');
    } catch (error) {
      console.warn('[Push] Failed to unregister push notifications.', error);
    } finally {
      isRegistered = false;
      lastSyncedDeviceFingerprint = '';
      await removePushDebugValue('token');
      updatePushDebugWindow({
        token: undefined,
      });
    }
  })();

  try {
    await unregisterPromise;
  } finally {
    unregisterPromise = null;
  }
};

export const initializePushNotifications = async () => {
  if (!isNativeAndroidApp()) {
    return;
  }

  await ensurePushListeners();

  if (!ENABLE_GENERATE_TOKEN_BEFORE_LOGIN && !cookies.get('token')) {
    await unregisterPushNotifications();
    return;
  }

  await registerPushNotifications();
  await syncNotificationDeviceRegistration();
};

export const getStoredPushToken = async () => getStoredPushDebugValue('token');
export const getStoredPendingPushTarget = async () => {
  const pendingTarget = await getStoredPendingPushTargetValue();

  updatePushDebugWindow({
    pendingTarget: pendingTarget || undefined,
  });

  return pendingTarget;
};

export const clearStoredPendingPushTarget = async () => {
  await clearStoredPendingPushTargetValue();
  updatePushDebugWindow({
    pendingTarget: undefined,
  });
};
